import { produce } from "immer";
import { intersection, union } from "lodash";
import getMissingRequiredFields, {
  isTemplateContentValid,
} from "notes-and-forms-v2/util/getMissingRequiredFields";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ClientServicesForClipboard } from "../client-services/ClientServices";
import { DiagnosisForClipboard } from "../diagnosis/Diagnosis";
import styles from "./TemplateContent.module.scss";
import AtomicElementView, {
  AtomicElementViewForClipboard,
} from "./atomic-element-view/AtomicElementView";
import SectionView, {
  SectionViewForClipboard,
} from "./section-view/SectionView";

export type TemplateContentImperativeHandleRef = {
  scrollFirstMissingElementIntoView: () => void;
  validate: () => boolean;
};

interface TemplateContentProps {
  templateElements: TemplateElement[];
  templateData: TemplateData;
  onChange?: (key: string, value: TemplateDataValue) => void;
  isPrintMode?: boolean;
  isDownloadMode?: boolean;
  isLocked?: boolean;
  disabled?: boolean;
  showSuicidalWarningInPhq9?: boolean;
  showCopyToClipboard?: boolean;
  options?: TemplateContentOptions;
  theme?: "session-review" | "default";
  skipValidation?: boolean;
  imperativeHandleRef?: React.MutableRefObject<TemplateContentImperativeHandleRef | null>;
}

export default function TemplateContent({
  templateElements,
  templateData,
  onChange = () =>
    console.warn("No change handler availabile in template content"),
  isPrintMode = false,
  isDownloadMode = false,
  isLocked = false,
  disabled = false,
  showSuicidalWarningInPhq9 = false,
  showCopyToClipboard = false,
  options = {},
  theme = "default",
  skipValidation = false,
  imperativeHandleRef,
}: TemplateContentProps) {
  const firstMissingElementRef = useRef<HTMLDivElement | null>(null);
  const [fieldIdsToValidate, setFieldIdsToValidate] = useState<string[]>([]);
  const [showAllMissingFields, setShowAllMissingFields] = useState(false);

  useImperativeHandle(
    imperativeHandleRef,
    () => ({
      scrollFirstMissingElementIntoView() {
        setTimeout(() => {
          firstMissingElementRef.current?.scrollIntoView();
        }, 0);
      },
      validate() {
        setShowAllMissingFields(true);
        if (isTemplateContentValid(templateElements, templateData)) {
          return true;
        }

        setTimeout(() => {
          firstMissingElementRef.current?.scrollIntoView({
            behavior: "smooth",
          });
        }, 0);
        return false;
      },
    }),
    [templateData, templateElements],
  );

  const fieldsToShowAsMissing = useMemo(() => {
    const allMissingFieldIds = getMissingRequiredFields(
      templateElements,
      templateData,
    );

    if (showAllMissingFields) {
      return allMissingFieldIds;
    }

    return intersection(allMissingFieldIds, fieldIdsToValidate);
  }, [
    showAllMissingFields,
    fieldIdsToValidate,
    templateElements,
    templateData,
  ]);

  const sectionNumbers = useMemo(
    () =>
      templateElements
        .filter((element) => element.type === "section")
        .reduce((acc, element, index) => ({ ...acc, [element.id]: index }), {}),
    [templateElements],
  );

  function onSignatureChange(elementId: string, newValue: SignatureData) {
    onChange(
      "signatures",
      templateData.signatures
        ? produce(templateData.signatures, (draft) => {
            draft[elementId] = newValue;
          })
        : { [elementId]: newValue },
    );
  }

  const triggerValidation = useCallback(
    (key: string) => {
      if (skipValidation) {
        return;
      }
      setFieldIdsToValidate((prev) => union(prev, [key]));
    },
    [skipValidation],
  );

  useEffect(() => {
    if (skipValidation) {
      setFieldIdsToValidate([]);
    }
  }, [skipValidation]);

  return (
    <div className={styles.container}>
      {templateElements.map((element) =>
        element.type === "section" ? (
          <SectionView
            key={element.id}
            element={element}
            data={templateData[element.id]}
            signatureData={templateData.signatures}
            onChange={(newValue) => onChange(element.id, newValue)}
            onSignatureChange={onSignatureChange}
            sectionIndex={sectionNumbers[element.id]}
            isPrintMode={isPrintMode}
            isDownloadMode={isDownloadMode}
            isLocked={isLocked}
            disabled={disabled}
            showSuicidalWarningInPhq9={showSuicidalWarningInPhq9}
            showCopyToClipboard={showCopyToClipboard}
            options={options}
            theme={theme}
            triggerValidation={triggerValidation}
            fieldsToShowAsMissing={fieldsToShowAsMissing}
            firstMissingElementRef={firstMissingElementRef}
          />
        ) : (
          <AtomicElementView
            ref={
              element.id === fieldsToShowAsMissing[0]
                ? firstMissingElementRef
                : null
            }
            key={element.id}
            element={element}
            data={templateData[element.id]}
            signatureData={templateData.signatures?.[element.id]}
            onChange={(newValue) => onChange(element.id, newValue)}
            onSignatureChange={onSignatureChange}
            isPrintMode={isPrintMode}
            isDownloadMode={isDownloadMode}
            isLocked={isLocked}
            disabled={disabled}
            showSuicidalWarningInPhq9={showSuicidalWarningInPhq9}
            showCopyToClipboard={showCopyToClipboard}
            options={options}
            theme={theme}
            triggerValidation={triggerValidation}
            showAsMissing={fieldsToShowAsMissing.includes(element.id)}
          />
        ),
      )}
    </div>
  );
}
export function TemplateContentForClipboard({
  title,
  templateElements,
  templateData,
  noteCodes,
  icdCodesList,
}: {
  title: string;
  templateElements: TemplateElement[];
  templateData: Record<string, TemplateDataValue>;
  noteCodes: NoteCodes;
  icdCodesList: ICDCodesList;
}) {
  return (
    <div>
      <h1>{title}</h1>
      <div className={styles.container}>
        {templateElements.map((element) =>
          element.type === "section" ? (
            <SectionViewForClipboard
              key={element.id}
              element={element}
              data={templateData[element.id]}
            />
          ) : (
            <AtomicElementViewForClipboard
              key={element.id}
              element={element}
              data={templateData[element.id]}
            />
          ),
        )}
      </div>
      <ClientServicesForClipboard noteCodes={noteCodes} />
      <DiagnosisForClipboard
        noteCodes={noteCodes}
        icdCodesList={icdCodesList}
      />
    </div>
  );
}
